import { supabase } from '../supabaseClient'

export const rptService = {
  // =====================================================
  // OWNERS MANAGEMENT
  // =====================================================
  
  async getOwners({ page = 0, pageSize = 20, search = '', sortBy = 'name_asc' } = {}) {
    try {
      console.log(`🔍 Fetching owners page=${page}, pageSize=${pageSize}, search="${search}", sortBy="${sortBy}"`)
      
      let query = supabase
        .from('rpt_owners')
        .select('*', { count: 'exact' })
        
      if (search) {
        const s = String(search).trim()
        if (s) {
          query = query.or(`owner_name.ilike.%${s}%,address.ilike.%${s}%`)
        }
      }
      
      switch (sortBy) {
        case 'name_asc':
          query = query.order('owner_name', { ascending: true })
          break
        case 'name_desc':
          query = query.order('owner_name', { ascending: false })
          break
        case 'address_asc':
          query = query.order('address', { ascending: true })
          break
        case 'address_desc':
          query = query.order('address', { ascending: false })
          break
        default:
          query = query.order('owner_name', { ascending: true })
          break
      }

      const from = page * pageSize
      const to = from + pageSize - 1
      
      const { data, count, error } = await query.range(from, to)

      if (error) throw error

      return { success: true, data: data || [], count: count || 0 }
    } catch (error) {
      console.error('Error getting owners paginated:', error)
      return { success: false, error: error.message, data: [], count: 0 }
    }
  },

  async getOwnersForExport({ search = '', sortBy = 'name_asc' } = {}) {
    try {
      let allOwners = []
      let from = 0
      const pageSize = 1000
      let hasMore = true

      while (hasMore) {
        let query = supabase
          .from('rpt_owners')
          .select('*')

        if (search) {
          const s = String(search).trim()
          if (s) {
            query = query.or(`owner_name.ilike.%${s}%,address.ilike.%${s}%`)
          }
        }

        switch (sortBy) {
          case 'name_asc':
            query = query.order('owner_name', { ascending: true })
            break
          case 'name_desc':
            query = query.order('owner_name', { ascending: false })
            break
          case 'address_asc':
            query = query.order('address', { ascending: true })
            break
          case 'address_desc':
            query = query.order('address', { ascending: false })
            break
          default:
            query = query.order('owner_name', { ascending: true })
            break
        }

        const { data, error } = await query.range(from, from + pageSize - 1)

        if (error) throw error

        if (data && data.length > 0) {
          allOwners = [...allOwners, ...data]
          from += pageSize
          hasMore = data.length === pageSize
        } else {
          hasMore = false
        }
      }

      return { success: true, data: allOwners }
    } catch (error) {
      console.error('Error exporting owners:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getAllOwners() {
    try {
      console.log('🔍 Fetching all owners from rpt_owners table...')
      let allOwners = []
      let from = 0
      const pageSize = 1000
      let hasMore = true

      while (hasMore) {
        console.log(`📄 Fetching page: from=${from}, pageSize=${pageSize}`)
        const { data, error } = await supabase
          .from('rpt_owners')
          .select('*')
          .order('owner_name', { ascending: true })
          .range(from, from + pageSize - 1)

        if (error) {
          console.error('❌ Error fetching owners:', error)
          throw error
        }

        console.log(`📊 Page result: count=${data?.length || 0}`)
        
        if (data && data.length > 0) {
          allOwners = [...allOwners, ...data]
          from += pageSize
          hasMore = data.length === pageSize
        } else {
          hasMore = false
        }
      }

      console.log(`✅ Loaded ${allOwners.length} total owners`)
      return { success: true, data: allOwners }
    } catch (error) {
      console.error('Error getting owners:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async addOwner(ownerData) {
    try {
      const { data, error } = await supabase
        .from('rpt_owners')
        .insert([ownerData])
        .select()

      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding owner:', error)
      return { success: false, error: error.message }
    }
  },

  async updateOwner(ownerId, ownerData) {
    try {
      const { data, error } = await supabase
        .from('rpt_owners')
        .update(ownerData)
        .eq('owner_id', ownerId)
        .select()

      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating owner:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteOwner(ownerId) {
    try {
      const { data, error } = await supabase
        .from('rpt_owners')
        .delete()
        .eq('owner_id', ownerId)
        .select()

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting owner:', error)
      return { success: false, error: error.message }
    }
  },

  // =====================================================
  // PROPERTIES MANAGEMENT
  // =====================================================
  
  // Optimized server-side pagination
  async getProperties({ page = 0, pageSize = 20, search = '', barangay = '', status = '', sortBy = 'owner', sortDir = 'asc' }) {
    try {
      console.log(`🔍 Fetching properties page=${page}, size=${pageSize}, search="${search}", brgy="${barangay}", status="${status}"`)
      
      let query = supabase
        .from('rpt_properties')
        .select(`
          *,
          owner:rpt_owners(owner_id, owner_name, address),
          kind:rpt_property_kinds(kind_id, kind_name),
          classification:rpt_property_classifications(classification_id, classification_name)
        `, { count: 'exact' })
        
      // Apply filters
      if (barangay) {
        query = query.eq('barangay', barangay)
      }

      if (status) {
        const currentYear = new Date().getFullYear();
        if (status === 'updated') {
          query = query.eq('last_payment_year', currentYear);
        } else if (status === 'due') {
          query = query.eq('last_payment_year', currentYear - 1);
        } else if (status === 'delinquent') {
          query = query.lt('last_payment_year', currentYear - 1);
        } else if (status === 'advance') {
          query = query.gt('last_payment_year', currentYear);
        } else if (status === 'no_payment') {
          query = query.is('last_payment_year', null);
        }
      }
      
      if (search) {
        const s = String(search).trim()
        if (s) {
          try {
            // Collect matching property IDs from property fields
            const { data: fieldMatches } = await supabase
              .from('rpt_properties')
              .select('property_id')
              .or(`td_number.ilike.%${s}%,pin.ilike.%${s}%,arpn.ilike.%${s}%,barangay.ilike.%${s}%`)
            const fieldIds = (fieldMatches || []).map(r => r.property_id)

            // Collect owner IDs that match search
            const { data: ownersMatch } = await supabase
              .from('rpt_owners')
              .select('owner_id')
              .ilike('owner_name', `%${s}%`)
            const ownerIds = (ownersMatch || []).map(o => o.owner_id)

            // Collect property IDs by matched owners
            let ownerPropIds = []
            if (ownerIds.length > 0) {
              const { data: ownerProps } = await supabase
                .from('rpt_properties')
                .select('property_id')
                .in('owner_id', ownerIds)
              ownerPropIds = (ownerProps || []).map(r => r.property_id)
            }

            // Union of all matched IDs
            const matchedIds = Array.from(new Set([...
              fieldIds,
              ...ownerPropIds
            ]))

            // If nothing matched, short-circuit
            if (matchedIds.length === 0) {
              return { success: true, data: [], count: 0 }
            }

            // Rebuild query to fetch only matched properties
            query = supabase
              .from('rpt_properties')
              .select(`
                *,
                owner:rpt_owners(owner_id, owner_name, address),
                kind:rpt_property_kinds(kind_id, kind_name),
                classification:rpt_property_classifications(classification_id, classification_name)
              `, { count: 'exact' })
              .in('property_id', matchedIds)

            // Apply barangay filter if present
            if (barangay) {
              query = query.eq('barangay', barangay)
            }
          } catch (e) {
            console.warn('Search processing error:', e)
            // Fallback to simple field search
            query = query.or(`td_number.ilike.%${s}%,pin.ilike.%${s}%,arpn.ilike.%${s}%,barangay.ilike.%${s}%`)
          }
        }
      }
      
      // Apply sorting
      const asc = String(sortDir).toLowerCase() !== 'desc'
      if (String(sortBy) === 'owner') {
        query = query.order('owner_name', { ascending: asc, foreignTable: 'rpt_owners' })
      } else if (String(sortBy) === 'td') {
        query = query.order('td_number', { ascending: asc })
      } else {
        query = query.order('owner_name', { ascending: asc, foreignTable: 'rpt_owners' })
      }

      // Apply pagination
      const from = page * pageSize
      const to = from + pageSize - 1
      
      const { data: properties, count, error } = await query
        .range(from, to)

      if (error) throw error

      if (!properties || properties.length === 0) {
        return { success: true, data: [], count: 0 }
      }

      // Enrich with Assessments and Payments (Only for this page)
      const propertyIds = properties.map(p => p.property_id)
      
      // 1. Fetch Assessments
      let assessmentMap = {}
      try {
        const { data: assessments } = await supabase
          .from('rpt_property_assessments')
          .select('property_id, assessed_value, effectivity_year')
          .in('property_id', propertyIds)
          .order('effectivity_year', { ascending: true })
        
        if (assessments) {
          assessmentMap = assessments.reduce((acc, curr) => {
            acc[curr.property_id] = curr
            return acc
          }, {})
        }
      } catch (e) { console.warn('Assessments fetch error:', e) }

      // 2. Fetch Latest Payments
      let paymentMap = {}
      try {
        const { data: payments } = await supabase
          .from('rpt_payments')
          .select('property_id, year_to')
          .in('property_id', propertyIds)
          .order('year_to', { ascending: false })
          // No limit needed here as we only have ~20 properties
        
        if (payments) {
          paymentMap = payments.reduce((acc, curr) => {
            const currentMax = acc[curr.property_id] || 0
            if (curr.year_to > currentMax) {
              acc[curr.property_id] = curr.year_to
            }
            return acc
          }, {})
        }
      } catch (e) { console.warn('Payments fetch error:', e) }

      // Merge data
      const enrichedData = properties.map(prop => ({
        ...prop,
        owner_name: prop.owner?.owner_name || null,
        owner_address: prop.owner?.address || null,
        kind_name: prop.kind?.kind_name || null,
        classification_name: prop.classification?.classification_name || null,
        assessed_value: assessmentMap[prop.property_id]?.assessed_value || null,
        effectivity_year: assessmentMap[prop.property_id]?.effectivity_year || null,
        last_payment_year: paymentMap[prop.property_id] || null
      }))

      return { success: true, data: enrichedData, count }

    } catch (error) {
      console.error('Error getting properties paginated:', error)
      return { success: false, error: error.message, data: [], count: 0 }
    }
  },

  async getAllProperties() {
    try {
      console.log('🔍 Fetching all properties from rpt_properties table...')
      let allProperties = []
      let from = 0
      const pageSize = 25 // Reduced to 25 to safely fit within URL length limits for IN filters
      let hasMore = true

      // Fetch all properties in pages
      while (hasMore) {
        console.log(`📄 Fetching properties page: from=${from}, pageSize=${pageSize}`)
        const { data: properties, error: propError } = await supabase
          .from('rpt_properties')
          .select(`
            *,
            owner:rpt_owners(owner_id, owner_name, address),
            kind:rpt_property_kinds(kind_id, kind_name),
            classification:rpt_property_classifications(classification_id, classification_name)
          `)
          .order('td_number', { ascending: true })
          .range(from, from + pageSize - 1)

        if (propError) {
          console.error('❌ Error fetching properties:', propError)
          throw propError
        }

        console.log(`📊 Properties page result: count=${properties?.length || 0}`)
        
        if (properties && properties.length > 0) {
          const propertyIds = properties.map(p => p.property_id)
          
          // 1. Fetch Assessments for this batch
          let assessmentMap = {}
          try {
            const { data: assessments, error: assessError } = await supabase
              .from('rpt_property_assessments')
              .select('property_id, assessed_value, effectivity_year')
              .in('property_id', propertyIds)
              .order('effectivity_year', { ascending: true })
            
            if (assessError) {
              console.warn('⚠️ Warning fetching assessments:', assessError)
            } else if (assessments) {
              assessmentMap = assessments.reduce((acc, curr) => {
                acc[curr.property_id] = curr
                return acc
              }, {})
            }
          } catch (e) { console.warn('Assessments fetch error:', e) }

          // 2. Fetch Latest Payments for this batch
          let paymentMap = {}
          try {
            const { data: payments, error: paymentError } = await supabase
              .from('rpt_payments')
              .select('property_id, year_to')
              .in('property_id', propertyIds)
              .order('year_to', { ascending: false })
              .limit(5000)
            
            if (paymentError) {
              console.warn('⚠️ Warning fetching payments:', paymentError)
            } else if (payments) {
              paymentMap = payments.reduce((acc, curr) => {
                const currentMax = acc[curr.property_id] || 0
                if (curr.year_to > currentMax) {
                  acc[curr.property_id] = curr.year_to
                }
                return acc
              }, {})
            }
          } catch (e) { console.warn('Payments fetch error:', e) }

          // Enrich this batch
          const enrichedBatch = properties.map(prop => ({
            ...prop,
            owner_name: prop.owner?.owner_name || null,
            owner_address: prop.owner?.address || null,
            kind_name: prop.kind?.kind_name || null,
            classification_name: prop.classification?.classification_name || null,
            assessed_value: assessmentMap[prop.property_id]?.assessed_value || null,
            effectivity_year: assessmentMap[prop.property_id]?.effectivity_year || null,
            last_payment_year: paymentMap[prop.property_id] || null
          }))

          allProperties = [...allProperties, ...enrichedBatch]
          from += pageSize
          hasMore = properties.length === pageSize
        } else {
          hasMore = false
        }
      }

      console.log(`✅ Loaded ${allProperties.length} total properties`)
      return { success: true, data: allProperties }
    } catch (error) {
      console.error('❌ Error getting properties:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getDelinquentProperties(statusFilter = 'delinquent') {
    try {
      console.log(`🔍 Generating report for status: ${statusFilter}...`);
      
      // 1. Fetch ALL properties (base data) - Optimized with larger pages
      let allProperties = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('rpt_properties')
          .select(`
            *,
            owner:rpt_owners(owner_id, owner_name, address),
            kind:rpt_property_kinds(kind_id, kind_name),
            classification:rpt_property_classifications(classification_id, classification_name)
          `)
          .range(from, from + pageSize - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          allProperties = [...allProperties, ...data];
          from += pageSize;
          hasMore = data.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      if (allProperties.length === 0) return { success: true, data: [] };

      // 2. Fetch Assessments (to get Assessed Value) - Batch fetch
      // We'll fetch all current assessments. 
      // Note: If dataset is huge, we might need to paginate this too, but let's assume it fits for now or use the same paging pattern.
      let assessmentMap = {};
      from = 0;
      hasMore = true;
      while (hasMore) {
        const { data, error } = await supabase
          .from('rpt_property_assessments') // Using historical table, but we need to filter for latest per property
          .select('property_id, assessed_value, effectivity_year')
          .order('effectivity_year', { ascending: false }) // Order by year desc to get latest first
          .range(from, from + pageSize - 1);
          
        if (error) throw error;

        if (data && data.length > 0) {
          data.forEach(a => {
            // Since we ordered by year desc, the first time we see a property_id, it's the latest
            if (!assessmentMap[a.property_id]) {
              assessmentMap[a.property_id] = a;
            }
          });
          from += pageSize;
          hasMore = data.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      // 3. Fetch Payments (to determine delinquency) - Batch fetch
      // We need the MAX year_to for each property
      let paymentMap = {};
      from = 0;
      hasMore = true;
      while (hasMore) {
        const { data, error } = await supabase
          .from('rpt_payments')
          .select('property_id, year_to')
          .range(from, from + pageSize - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          data.forEach(p => {
            if (!paymentMap[p.property_id] || p.year_to > paymentMap[p.property_id]) {
              paymentMap[p.property_id] = p.year_to;
            }
          });
          from += pageSize;
          hasMore = data.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      // 4. Filter and Enriched
      const currentYear = new Date().getFullYear();
      const filteredProperties = allProperties
        .map(prop => {
          const assessment = assessmentMap[prop.property_id];
          const lastPaymentYear = paymentMap[prop.property_id];
          
          return {
            ...prop,
            owner_name: prop.owner?.owner_name || null,
            owner_address: prop.owner?.address || null,
            kind_name: prop.kind?.kind_name || null,
            classification_name: prop.classification?.classification_name || null,
            assessed_value: assessment?.assessed_value || null,
            effectivity_year: assessment?.effectivity_year || null,
            last_payment_year: lastPaymentYear || null
          };
        })
        .filter(prop => {
          // If filtering by specific status, apply logic
          const lastYear = prop.last_payment_year;
          
          if (statusFilter === 'updated') {
            return lastYear === currentYear;
          } else if (statusFilter === 'due') {
            return lastYear === currentYear - 1;
          } else if (statusFilter === 'delinquent') {
             // Exclude properties with no payment history for delinquency report (as per original logic)
            if (!lastYear) return false;
            return lastYear < currentYear - 1;
          } else if (statusFilter === 'advance') {
            return lastYear > currentYear;
          } else if (statusFilter === 'no_payment') {
            return !lastYear;
          }

          // Default (all if no valid status provided, though defaulting to delinquent in args)
          return true;
        });

      console.log(`🚨 Found ${filteredProperties.length} properties for status: ${statusFilter}`);
      return { success: true, data: filteredProperties };

    } catch (error) {
      console.error('❌ Error getting report properties:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  async getAllBarangays() {
    try {
      let all = []
      let from = 0
      const pageSize = 1000
      let hasMore = true

      while (hasMore) {
        const { data, error } = await supabase
          .from('rpt_properties')
          .select('barangay')
          .order('barangay', { ascending: true })
          .range(from, from + pageSize - 1)

        if (error) throw error

        if (data && data.length > 0) {
          all = all.concat(data)
          from += pageSize
          hasMore = data.length === pageSize
        } else {
          hasMore = false
        }
      }

      const list = Array.from(new Set((all || []).map(r => String(r.barangay || '').trim()).filter(Boolean))).sort()
      return { success: true, data: list }
    } catch (error) {
      console.error('Error getting barangays:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getPropertyById(propertyId) {
    try {
      const { data: prop, error } = await supabase
        .from('rpt_properties')
        .select(`
          *,
          owner:rpt_owners(owner_id, owner_name),
          kind:rpt_property_kinds(kind_id, kind_name),
          classification:rpt_property_classifications(classification_id, classification_name)
        `)
        .eq('property_id', propertyId)
        .single()

      if (error) throw error

      // Fetch current assessment for this property
      let assessed = null
      const { data: assessment } = await supabase
        .from('rpt_current_assessments')
        .select('property_id, assessed_value, effectivity_year')
        .eq('property_id', propertyId)
        .single()

      if (assessment) {
        assessed = assessment
      }

      // Flatten response and add assessment fields
      const flattened = {
        ...prop,
        owner_name: prop.owner?.owner_name || null,
        kind_name: prop.kind?.kind_name || null,
        classification_name: prop.classification?.classification_name || null,
        assessed_value: assessed?.assessed_value || null,
        effectivity_year: assessed?.effectivity_year || null
      }

      return { success: true, data: flattened }
    } catch (error) {
      console.error('Error getting property:', error)
      return { success: false, error: error.message }
    }
  },

  async getPropertiesByOwner(ownerId) {
    try {
      // Get properties for this owner
      const { data: properties, error: propError } = await supabase
        .from('rpt_properties')
        .select(`
          *,
          owner:rpt_owners(owner_id, owner_name),
          kind:rpt_property_kinds(kind_id, kind_name),
          classification:rpt_property_classifications(classification_id, classification_name)
        `)
        .eq('owner_id', ownerId)
        .order('td_number', { ascending: true })

      if (propError) throw propError
      
      // Get property IDs to fetch assessments
      const propertyIds = (properties || []).map(p => p.property_id)
      
      // Get current assessments for these properties
      const { data: assessments, error: assessError } = await supabase
        .from('rpt_current_assessments')
        .select('property_id, assessed_value, effectivity_year')
        .in('property_id', propertyIds)

      if (assessError) console.error('Error fetching assessments:', assessError)
      
      // Create a map of property_id to assessment
      const assessmentMap = (assessments || []).reduce((acc, assessment) => {
        acc[assessment.property_id] = assessment
        return acc
      }, {})
      
      // Flatten the joined data and add current assessment
      const flattenedData = (properties || []).map(prop => ({
        ...prop,
        owner_name: prop.owner?.owner_name || null,
        kind_name: prop.kind?.kind_name || null,
        classification_name: prop.classification?.classification_name || null,
        assessed_value: assessmentMap[prop.property_id]?.assessed_value || null,
        effectivity_year: assessmentMap[prop.property_id]?.effectivity_year || null
      }))
      
      return { success: true, data: flattenedData }
    } catch (error) {
      console.error('Error getting properties by owner:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async addProperty(propertyData) {
    try {
      const cleanedData = {}
      const numericFields = ['market_value', 'assessment_level', 'area']
      const yearFields = ['last_payment_year']

      Object.keys(propertyData || {}).forEach(key => {
        const val = propertyData[key]
        if (val === '' || val === undefined) {
          cleanedData[key] = null
        } else if (numericFields.includes(key)) {
          if (typeof val === 'string' && val.trim() !== '') {
            const num = parseFloat(val)
            cleanedData[key] = isNaN(num) ? val : num
          } else {
            cleanedData[key] = val
          }
        } else if (yearFields.includes(key)) {
          if (typeof val === 'string' && val.trim() !== '') {
            const num = parseInt(val, 10)
            cleanedData[key] = isNaN(num) ? val : num
          } else {
            cleanedData[key] = val
          }
        } else {
          cleanedData[key] = val
        }
      })

      const { data, error } = await supabase
        .from('rpt_properties')
        .insert([cleanedData])
        .select()

      if (error) {
        console.error('Error adding property to Supabase:', error)
        throw error
      }

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding property:', error)
      return { success: false, error: error.message || error }
    }
  },

  async updateProperty(propertyId, propertyData) {
    try {
      const cleanedData = {}
      const numericFields = ['market_value', 'assessment_level', 'area']
      const yearFields = ['last_payment_year']

      Object.keys(propertyData || {}).forEach(key => {
        const val = propertyData[key]
        if (val === '' || val === undefined) {
          cleanedData[key] = null
        } else if (numericFields.includes(key)) {
          if (typeof val === 'string' && val.trim() !== '') {
            const num = parseFloat(val)
            cleanedData[key] = isNaN(num) ? val : num
          } else {
            cleanedData[key] = val
          }
        } else if (yearFields.includes(key)) {
          if (typeof val === 'string' && val.trim() !== '') {
            const num = parseInt(val, 10)
            cleanedData[key] = isNaN(num) ? val : num
          } else {
            cleanedData[key] = val
          }
        } else {
          cleanedData[key] = val
        }
      })

      const { data, error } = await supabase
        .from('rpt_properties')
        .update(cleanedData)
        .eq('property_id', propertyId)
        .select()

      if (error) {
        console.error('Error updating property in Supabase:', error)
        throw error
      }

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating property:', error)
      return { success: false, error: error.message || error }
    }
  },

  async deleteProperty(propertyId) {
    try {
      const { data, error } = await supabase
        .from('rpt_properties')
        .delete()
        .eq('property_id', propertyId)
        .select()

      if (error) throw error

      return { success: true }
    } catch (error) {
      console.error('Error deleting property:', error)
      return { success: false, error: error.message }
    }
  },

  // =====================================================
  // PROPERTY ASSESSMENTS (HISTORICAL VALUES)
  // =====================================================

  async getPropertyAssessments(propertyId) {
    try {
      const { data, error } = await supabase
        .from('rpt_property_assessments')
        .select('*')
        .eq('property_id', propertyId)
        .order('effectivity_year', { ascending: false })

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting property assessments:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getAllPropertyAssessments() {
    try {
      let all = []
      let from = 0
      const pageSize = 1000
      let hasMore = true

      while (hasMore) {
        const { data, error } = await supabase
          .from('rpt_property_assessments')
          .select('*')
          .order('effectivity_year', { ascending: false })
          .range(from, from + pageSize - 1)

        if (error) throw error

        if (data && data.length > 0) {
          all = all.concat(data)
          from += pageSize
          hasMore = data.length === pageSize
        } else {
          hasMore = false
        }
      }

      return { success: true, data: all }
    } catch (error) {
      console.error('Error getting all property assessments:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async addPropertyAssessment(assessmentData) {
    try {
      const cleanedData = {}
      const numericFields = ['assessed_value']
      const yearFields = ['effectivity_year']

      Object.keys(assessmentData || {}).forEach(key => {
        const val = assessmentData[key]
        if (val === '' || val === undefined) {
          cleanedData[key] = null
        } else if (numericFields.includes(key)) {
          if (typeof val === 'string' && val.trim() !== '') {
            const num = parseFloat(val)
            cleanedData[key] = isNaN(num) ? val : num
          } else {
            cleanedData[key] = val
          }
        } else if (yearFields.includes(key)) {
          if (typeof val === 'string' && val.trim() !== '') {
            const num = parseInt(val, 10)
            cleanedData[key] = isNaN(num) ? val : num
          } else {
            cleanedData[key] = val
          }
        } else {
          cleanedData[key] = val
        }
      })

      const { data, error } = await supabase
        .from('rpt_property_assessments')
        .insert([cleanedData])
        .select()

      if (error) {
        console.error('Error adding assessment to Supabase:', error)
        throw error
      }

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding assessment:', error)
      return { success: false, error: error.message || error }
    }
  },

  async updatePropertyAssessment(assessmentId, assessmentData) {
    try {
      const { data, error } = await supabase
        .from('rpt_property_assessments')
        .update(assessmentData)
        .eq('assessment_id', assessmentId)
        .select()

      if (error) throw error

      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating assessment:', error)
      return { success: false, error: error.message }
    }
  },

  async deletePropertyAssessment(assessmentId) {
    try {
      const { data, error } = await supabase
        .from('rpt_property_assessments')
        .delete()
        .eq('assessment_id', assessmentId)

      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting assessment:', error)
      return { success: false, error: error.message }
    }
  },

  async getAssessedValueForYear(propertyId, year) {
    try {
      // Get the most recent assessment effective for the given year
      const { data, error } = await supabase
        .from('rpt_property_assessments')
        .select('assessed_value, effectivity_year')
        .eq('property_id', propertyId)
        .lte('effectivity_year', year)
        .order('effectivity_year', { ascending: false })
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') throw error // Ignore "not found" error
      return { success: true, data: data || null }
    } catch (error) {
      console.error('Error getting assessed value for year:', error)
      return { success: false, error: error.message, data: null }
    }
  },

  async getCurrentAssessment(propertyId) {
    try {
      // Get the most recent assessment (current value)
      const { data, error } = await supabase
        .from('rpt_property_assessments')
        .select('*')
        .eq('property_id', propertyId)
        .order('effectivity_year', { ascending: false })
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') throw error
      return { success: true, data: data || null }
    } catch (error) {
      console.error('Error getting current assessment:', error)
      return { success: false, error: error.message, data: null }
    }
  },

  // =====================================================
  // ASSESSMENTS MANAGEMENT
  // =====================================================
  
  async getAllAssessments(year = null) {
    try {
      let query = supabase
        .from('rpt_assessments')
        .select(`
          *,
          property:rpt_properties(td_number, barangay, property_class),
          owner:rpt_owners(first_name, last_name)
        `)
        .order('assessment_year', { ascending: false })
        .limit(10000)

      if (year) {
        query = query.eq('assessment_year', year)
      }

      const { data, error } = await query

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting assessments:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getAssessmentsByProperty(propertyId) {
    try {
      const { data, error } = await supabase
        .from('rpt_assessments')
        .select('*')
        .eq('property_id', propertyId)
        .order('assessment_year', { ascending: false })

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting property assessments:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async createAssessment(assessmentData) {
    try {
      const { data, error } = await supabase
        .from('rpt_assessments')
        .insert([assessmentData])
        .select()

      if (error) throw error
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error creating assessment:', error)
      return { success: false, error: error.message }
    }
  },

  async updateAssessment(assessmentId, assessmentData) {
    try {
      const { data, error } = await supabase
        .from('rpt_assessments')
        .update(assessmentData)
        .eq('assessment_id', assessmentId)
        .select()

      if (error) throw error
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating assessment:', error)
      return { success: false, error: error.message }
    }
  },

  // =====================================================
  // PAYMENTS MANAGEMENT
  // =====================================================
  
  async getAllPayments(startDate = null, endDate = null) {
    try {
      let all = []
      let from = 0
      const pageSize = 1000
      let hasMore = true

      while (hasMore) {
        let query = supabase
          .from('rpt_payments')
          .select(`
            *,
            property:rpt_properties(property_id, td_number, barangay, pin),
            owner:rpt_owners(owner_id, owner_name)
          `)
          .order('payment_date', { ascending: false })
          .range(from, from + pageSize - 1)

        if (startDate && endDate) {
          query = query.gte('payment_date', startDate).lte('payment_date', endDate)
        }

        const { data, error } = await query

        if (error) throw error

        if (data && data.length > 0) {
          all = all.concat(data)
          from += pageSize
          hasMore = data.length === pageSize
        } else {
          hasMore = false
        }
      }

      return { success: true, data: all }
    } catch (error) {
      console.error('Error getting payments:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async addPayment(paymentData) {
    try {
      console.log('RPT Service - Attempting to insert payment:', paymentData)
      
      const { data, error } = await supabase
        .from('rpt_payments')
        .insert([paymentData])
        .select()

      console.log('RPT Service - Insert result:', { data, error })

      if (error) {
        console.error('RPT Service - Insert error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        })
        throw error
      }
      
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('RPT Service - Caught error:', error)
      return { success: false, error: error.message, details: error }
    }
  },

  async recordLastPaymentYear(propertyId, ownerId, year) {
    try {
      const today = new Date().toISOString().split('T')[0]
      const paymentData = {
        property_id: propertyId,
        owner_id: ownerId,
        payment_date: today,
        year_from: year,
        year_to: year,
        amount_paid: 0,
        payment_method: 'YearOnly',
        or_number: null,
        year_breakdown: [],
        processed_by: 'System',
        remarks: `Year-only entry to mark last payment year: ${year}`
      }

      const result = await this.addPayment(paymentData)
      return result
    } catch (error) {
      console.error('Error recording last payment year:', error)
      return { success: false, error: error.message }
    }
  },

  async deletePayment(paymentId) {
    try {
      // Try by common primary key 'id'
      let { error } = await supabase
        .from('rpt_payments')
        .delete()
        .eq('id', paymentId)

      // If failed, attempt by 'payment_id'
      if (error) {
        const attempt2 = await supabase
          .from('rpt_payments')
          .delete()
          .eq('payment_id', paymentId)
        error = attempt2.error
      }

      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting payment:', error)
      return { success: false, error: error.message }
    }
  },

  async getPaymentsByAssessment(assessmentId) {
    try {
      const { data, error } = await supabase
        .from('rpt_payments')
        .select('*')
        .eq('assessment_id', assessmentId)
        .order('payment_date', { ascending: false })

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting payments:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getPaymentsByProperty(propertyId) {
    try {
      const { data, error } = await supabase
        .from('rpt_payments')
        .select(`
          *,
          processed_by
        `)
        .eq('property_id', propertyId)
        .order('payment_date', { ascending: false })

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting payments by property:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getPaymentsForProperties(propertyIds, { year } = {}) {
    try {
      if (!Array.isArray(propertyIds) || propertyIds.length === 0) {
        return { success: true, data: [] }
      }

      let query = supabase
        .from('rpt_payments')
        .select('property_id, amount_paid, year_from, year_to, year_breakdown, payment_date, or_number, processed_by, remarks, payment_method')
        .in('property_id', propertyIds)
        .order('payment_date', { ascending: false })
        .limit(5000)

      if (year !== undefined && year !== null && year !== '') {
        query = query.lte('year_from', year).gte('year_to', year)
      }

      const { data, error } = await query
      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting payments for properties:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async searchProperties(searchTerm) {
    try {
      const baseSelect = `
          *,
          owner:rpt_owners(owner_id, owner_name),
          kind:rpt_property_kinds(kind_id, kind_name),
          classification:rpt_property_classifications(classification_id, classification_name)
        `

      let properties = []

      if (searchTerm) {
        // 1) Search main property fields
        const { data: propsByFields, error: fieldsError } = await supabase
          .from('rpt_properties')
          .select(baseSelect)
          .or(`td_number.ilike.%${searchTerm}%,pin.ilike.%${searchTerm}%,arpn.ilike.%${searchTerm}%,barangay.ilike.%${searchTerm}%`)
          .order('td_number', { ascending: true })
          .limit(50)

        if (fieldsError) throw fieldsError

        // 2) Search owners by name, then fetch their properties
        const { data: ownersByName, error: ownersError } = await supabase
          .from('rpt_owners')
          .select('owner_id, owner_name')
          .ilike('owner_name', `%${searchTerm}%`)
          .limit(50)

        if (ownersError) throw ownersError

        let propsByOwner = []
        if (ownersByName && ownersByName.length > 0) {
          const ownerIds = ownersByName.map(o => o.owner_id)
          const { data: ownerProps, error: ownerPropsError } = await supabase
            .from('rpt_properties')
            .select(baseSelect)
            .in('owner_id', ownerIds)
            .order('td_number', { ascending: true })
            .limit(100)
          if (ownerPropsError) throw ownerPropsError
          propsByOwner = ownerProps || []
        }

        // Merge and dedupe by property_id
        const map = new Map()
        ;[...(propsByFields || []), ...propsByOwner].forEach(p => {
          map.set(p.property_id, p)
        })
        properties = Array.from(map.values())
      } else {
        // No search term: show a default subset
        const { data: defaultProps, error } = await supabase
          .from('rpt_properties')
          .select(baseSelect)
          .order('td_number', { ascending: true })
          .limit(25)

        if (error) throw error
        properties = defaultProps || []
      }
      if (properties && properties.length > 0) {
        const propertyIds = properties.map(p => p.property_id)
        
        // 1. Fetch Assessments
        let assessmentMap = {}
        const { data: assessments } = await supabase
          .from('rpt_current_assessments')
          .select('property_id, assessed_value, effectivity_year')
          .in('property_id', propertyIds)
        
        if (assessments) {
          assessmentMap = assessments.reduce((acc, curr) => {
            acc[curr.property_id] = curr
            return acc
          }, {})
        }

        // 2. Fetch Latest Payments
        let paymentMap = {}
        const { data: payments } = await supabase
          .from('rpt_payments')
          .select('property_id, year_to')
          .in('property_id', propertyIds)
        
        if (payments) {
          paymentMap = payments.reduce((acc, curr) => {
            const currentMax = acc[curr.property_id] || 0
            if (curr.year_to > currentMax) {
              acc[curr.property_id] = curr.year_to
            }
            return acc
          }, {})
        }

        const enrichedData = properties.map(prop => ({
          ...prop,
          owner_name: prop.owner?.owner_name || null,
          kind_name: prop.kind?.kind_name || null,
          classification_name: prop.classification?.classification_name || null,
          assessed_value: assessmentMap[prop.property_id]?.assessed_value || null,
          effectivity_year: assessmentMap[prop.property_id]?.effectivity_year || null,
          last_payment_year: paymentMap[prop.property_id] || null
        }))
        
        return { success: true, data: enrichedData }
      }

      return { success: true, data: [] }
    } catch (error) {
      console.error('Error searching properties:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  // =====================================================
  // SETTINGS MANAGEMENT
  // =====================================================
  
  async getSettings() {
    try {
      const { data, error } = await supabase
        .from('rpt_settings')
        .select('*')

      if (error) throw error
      
      // Convert array to object for easier access
      const settings = {}
      data.forEach(setting => {
        settings[setting.setting_key] = setting.setting_value
      })
      
      return { success: true, data: settings }
    } catch (error) {
      console.error('Error getting settings:', error)
      return { success: false, error: error.message, data: {} }
    }
  },

  async updateSetting(settingKey, settingValue) {
    try {
      const { data, error } = await supabase
        .from('rpt_settings')
        .update({ setting_value: settingValue })
        .eq('setting_key', settingKey)
        .select()

      if (error) throw error
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating setting:', error)
      return { success: false, error: error.message }
    }
  },

  // =====================================================
  // TAX RATES MANAGEMENT
  // =====================================================
  
  async getTaxRates(year = null) {
    try {
      let query = supabase
        .from('rpt_tax_rates')
        .select('*')
        .order('effectivity_year', { ascending: false })

      if (year) {
        query = query.eq('effectivity_year', year)
      }

      const { data, error } = await query

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting tax rates:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async addTaxRate(rateData) {
    try {
      const { data, error } = await supabase
        .from('rpt_tax_rates')
        .insert([rateData])
        .select()

      if (error) throw error
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding tax rate:', error)
      return { success: false, error: error.message }
    }
  },

  // =====================================================
  // RECENT DATA (DASHBOARD)
  // =====================================================

  async getRecentPayments(limit = 5) {
    try {
      const { data, error } = await supabase
        .from('rpt_payments')
        .select(`
          *,
          property:rpt_properties(td_number, barangay, pin),
          owner:rpt_owners(owner_name)
        `)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting recent payments:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getRecentProperties(limit = 5) {
    try {
      const { data, error } = await supabase
        .from('rpt_properties')
        .select(`
          *,
          owner:rpt_owners(owner_name),
          kind:rpt_property_kinds(kind_name),
          classification:rpt_property_classifications(classification_name)
        `)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting recent properties:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getRecentOwners(limit = 5) {
    try {
      const { data, error } = await supabase
        .from('rpt_owners')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting recent owners:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  // =====================================================
  // STATISTICS & REPORTS
  // =====================================================
  
  async getDashboardStats() {
    try {
      // Get total properties
      const { count: totalProperties } = await supabase
        .from('rpt_properties')
        .select('*', { count: 'exact', head: true })

      // Get total owners (no status filter - rpt_owners table doesn't have status column)
      const { count: totalOwners } = await supabase
        .from('rpt_owners')
        .select('*', { count: 'exact', head: true })

      // Get current year unpaid assessments
      const currentYear = new Date().getFullYear()
      const { count: unpaidAssessments } = await supabase
        .from('rpt_assessments')
        .select('*', { count: 'exact', head: true })
        .eq('assessment_year', currentYear)
        .neq('payment_status', 'Paid')

      // Get total collections this year
      const { data: payments, error: paymentsError } = await supabase
        .from('rpt_payments')
        .select('amount_paid')
        .gte('payment_date', `${currentYear}-01-01`)
        .lte('payment_date', `${currentYear}-12-31`)

      if (paymentsError) throw paymentsError

      const totalCollections = payments?.reduce((sum, p) => sum + parseFloat(p.amount_paid || 0), 0) || 0

      return {
        success: true,
        data: {
          totalProperties: totalProperties || 0,
          totalOwners: totalOwners || 0,
          unpaidAssessments: unpaidAssessments || 0,
          totalCollections,
          currentYear
        }
      }
    } catch (error) {
      console.error('Error getting dashboard stats:', error)
      return { success: false, error: error.message }
    }
  },

  async getCollectionReport(startDate, endDate) {
    try {
      const { data, error } = await supabase
        .from('rpt_payments')
        .select(`
          *,
          property:rpt_properties(td_number, barangay, property_class),
          owner:rpt_owners(first_name, last_name)
        `)
        .gte('payment_date', startDate)
        .lte('payment_date', endDate)
        .order('payment_date', { ascending: true })

      if (error) throw error

      const totalCollected = data?.reduce((sum, p) => sum + parseFloat(p.amount_paid || 0), 0) || 0

      return {
        success: true,
        data: {
          payments: data || [],
          totalCollected,
          count: data?.length || 0
        }
      }
    } catch (error) {
      console.error('Error getting collection report:', error)
      return { success: false, error: error.message }
    }
  },

  // Calculate tax based on property class and assessed value
  calculateTax(assessedValue, propertyClass, taxRates) {
    const rate = taxRates.find(r => r.property_class === propertyClass)
    
    if (!rate) {
      return {
        basic_tax: 0,
        sef_tax: 0,
        total_tax: 0
      }
    }

    const basic_tax = assessedValue * rate.basic_rate
    const sef_tax = assessedValue * rate.sef_rate
    const total_tax = basic_tax + sef_tax

    return {
      basic_tax: parseFloat(basic_tax.toFixed(2)),
      sef_tax: parseFloat(sef_tax.toFixed(2)),
      total_tax: parseFloat(total_tax.toFixed(2)),
      basic_rate: rate.basic_rate,
      sef_rate: rate.sef_rate
    }
  },

  // =====================================================
  // LOOKUP DATA
  // =====================================================

  async getPropertyKinds() {
    try {
      const { data, error } = await supabase
        .from('rpt_property_kinds')
        .select('*')
        .eq('is_active', true)
        .order('kind_name', { ascending: true })

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting property kinds:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getPropertyClassifications() {
    try {
      const { data, error } = await supabase
        .from('rpt_property_classifications')
        .select('*')
        .eq('is_active', true)
        .order('classification_name', { ascending: true })

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting property classifications:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  // =====================================================
  // AMNESTY MANAGEMENT
  // =====================================================

  async getAllAmnesty() {
    try {
      const { data, error } = await supabase
        .from('rpt_amnesty')
        .select('*')
        .order('tax_year_from', { ascending: false })

      if (error) throw error
      return { success: true, data: data || [] }
    } catch (error) {
      console.error('Error getting amnesty records:', error)
      return { success: false, error: error.message, data: [] }
    }
  },

  async getAmnestyByYear(taxYear) {
    try {
      const { data, error } = await supabase
        .from('rpt_amnesty')
        .select('*')
        .lte('tax_year_from', taxYear)
        .gte('tax_year_to', taxYear)
        .limit(1)
        .single()

      if (error && error.code !== 'PGRST116') throw error // Ignore "not found" error
      return { success: true, data: data || null }
    } catch (error) {
      console.error('Error getting amnesty for year:', error)
      return { success: false, error: error.message, data: null }
    }
  },

  async addAmnesty(amnestyData) {
    try {
      const { data, error } = await supabase
        .from('rpt_amnesty')
        .insert([amnestyData])
        .select()

      if (error) throw error
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error adding amnesty:', error)
      return { success: false, error: error.message }
    }
  },

  async updateAmnesty(amnestyId, amnestyData) {
    try {
      const { data, error } = await supabase
        .from('rpt_amnesty')
        .update(amnestyData)
        .eq('amnesty_id', amnestyId)
        .select()

      if (error) throw error
      return { success: true, data: data[0] }
    } catch (error) {
      console.error('Error updating amnesty:', error)
      return { success: false, error: error.message }
    }
  },

  async deleteAmnesty(amnestyId) {
    try {
      const { data, error } = await supabase
        .from('rpt_amnesty')
        .delete()
        .eq('amnesty_id', amnestyId)
        .select()

      if (error) throw error
      return { success: true }
    } catch (error) {
      console.error('Error deleting amnesty:', error)
      return { success: false, error: error.message }
    }
  }
}
